import React, { useCallback, useMemo } from "react";

import TopPanelView from "./TopPanelView";
import {
  DEFAULT_BOARD_SORT,
  type BoardSortDirection,
  type BoardSortOption,
  type BoardSortState,
} from "@/features/player-kanban/constants/boardSort";
import { resolvePipelineCategoryLabel } from "@/features/player-kanban/constants/pipelineCategories";
import { useAddPlayersPanelState } from "@/features/player-kanban/hooks/useAddPlayersPanelState";
import { useBoardCardOrderActions } from "@/features/player-kanban/hooks/useBoardCardOrderActions";
import { useBoardToolbar } from "@/features/player-kanban/hooks/useBoardToolbar";
import { useBoardManagement } from "@/features/player-kanban/hooks/useBoardManagement";
import { useBoardNameEditing } from "@/features/player-kanban/hooks/useBoardNameEditing";
import { useKanbanColors } from "@/features/player-kanban/hooks/useKanbanColors";
import useTagManagement from "@/features/player-kanban/hooks/useTagManagement";
import type { BoardInfo } from "@/features/player-kanban/components/organisms/BoardTabs/BoardTabsContainer";
import type { BoardViewMode } from "@/features/player-kanban/types/types";

import {
  KanbanPipelineTemplate,
  UserKanbanBoardRole,
} from "@/generated/graphql";

/**
 * Props for the TopPanelContainer component
 * @property boardId - The current kanban board ID for tag color context
 * @property boardName - The current kanban board name to display and edit
 * @property boards - Array of available kanban boards for tab selection
 * @property canEdit - Whether current user has permission to edit the board
 * @property isOwner - Whether current user is the board owner with full permissions
 * @property canShare - Whether current user has permission to share the board
 * @property userRole - The current user's role on this kanban board
 * @property onUpdateBoardName - Async callback fired when user saves a new board name
 * @property onBoardSelect - Callback fired when user selects a different board tab
 * @property onCreateBoard - Callback fired when user clicks create new board button
 * @property onMoreBoardOptions - Callback fired when user clicks board options menu
 * @property onDeleteBoard - Async callback fired when user confirms board deletion
 * @property onSharePress - Callback fired when user clicks share board button
 * @property viewMode - Which renderer the board is currently showing (SE-5491)
 * @property onViewModeChange - Callback fired with the newly selected renderer (SE-5491)
 */
interface TopPanelContainerProps {
  boardId?: string;
  boardName?: string;
  boards?: BoardInfo[];
  /** SE-6575: whether boards exist beyond those loaded (page cap / failed page). */
  hasUnreachableBoards?: boolean;
  isRosterBoard?: boolean;
  /**
   * SE-5489: whether the active surface is the migrated PIPELINE surface
   * (SE-6071). Forks the center region between the pipeline chip switcher and
   * the legacy BoardTabs strip.
   */
  isPipelineSurface?: boolean;
  playerCount?: number;
  canEdit?: boolean;
  isOwner?: boolean;
  canShare?: boolean;
  userRole?: UserKanbanBoardRole | null;
  onUpdateBoardName?: (newName: string) => Promise<void>;
  onBoardSelect?: (boardId: string) => void;
  onCreateBoard?: () => void;
  onMoreBoardOptions?: () => void;
  onDeleteBoard?: (boardId: string) => Promise<void>;
  onSharePress?: () => void;
  viewMode?: BoardViewMode;
  onViewModeChange?: (mode: BoardViewMode) => void;
  /**
   * SE-6488: the board's pipeline template — resolves the category tag chip
   * rendered beside the pipeline name in place of the removed subtitle.
   */
  pipelineTemplate?: KanbanPipelineTemplate | null;
  /** SE-6488: board-scoped player search text. */
  boardSearchQuery?: string;
  /** SE-6488: fired as the user types in the toolbar's search input. */
  onBoardSearchChange?: (value: string) => void;
  /** SE-6488: the board-level sort selection. */
  boardSort?: BoardSortState;
  /** SE-6488: fired with the newly selected board sort field. */
  onBoardSortChange?: (option: BoardSortOption) => void;
  /** SE-6554: fired with the newly selected board sort direction. */
  onBoardSortDirectionChange?: (direction: BoardSortDirection) => void;
  /**
   * SE-7144: when present AND the TABLE view is active, the Filters button
   * opens the Watchlist-parity panel this callback owns instead of the
   * board's own FilterModal (which stays the Kanban surface's panel).
   */
  onTableFiltersPress?: () => void;
  /** SE-7144: committed filter count for the Table view's Filters badge. */
  tableActiveFilterCount?: number;
  /**
   * SE-7405: when present AND the KANBAN view is active, the Filters button
   * opens the same Watchlist-parity panel Table uses (owned by MainContainer)
   * instead of the board's own FilterModal — and, unlike the FilterModal,
   * committing it actually narrows the visible board columns, mirroring
   * `onTableFiltersPress` exactly.
   */
  onKanbanFiltersPress?: () => void;
  /** SE-7405: committed filter count for the Kanban view's Filters badge. */
  kanbanActiveFilterCount?: number;
}

/** Inert sort handler for a caller that supplies no board sort wiring. */
const noOpSort = () => undefined;

/**
 * Which surface owns the toolbar's Filters affordance — SE-7144 (Table) or
 * SE-7405 (Kanban). These are the only two, because {@link BoardViewMode} has
 * only two values and each one has a Watchlist-parity drawer behind it.
 *
 * SE-7470 removed a third arm, `legacy`, which routed the button at the
 * board's own FilterModal. That arm had been unreachable since SE-7405: it was
 * selected only when the view's callback was absent, and MainContainer has
 * always supplied both unconditionally. See the module note on the deletion.
 */
type FilterMode = BoardViewMode;

/**
 * Resolves {@link FilterMode} from the view mode alone — a total function over
 * a two-value union, so there is no fallback arm to leave unreachable.
 *
 * An absent `viewMode` resolves to `kanban` to match `usePipelineViewMode`'s
 * own `DEFAULT_VIEW_MODE`, so the toolbar and the board agree about which
 * renderer is showing before the caller has committed to one.
 * @param viewMode - Which renderer the board is showing (SE-5491).
 * @returns The resolved {@link FilterMode}.
 */
function resolveFilterMode(viewMode: BoardViewMode | undefined): FilterMode {
  return viewMode === "table" ? "table" : "kanban";
}

/**
 * Container component for the top panel that orchestrates board management, filters, and editing
 * @param props - The component props
 * @param props.boardId - The current kanban board ID for tag color context
 * @param props.boardName - The current kanban board name to display and edit
 * @param props.boards - Array of available kanban boards for tab selection
 * @param props.hasUnreachableBoards - Whether boards exist beyond those loaded (SE-6575)
 * @param props.canEdit - Whether current user has permission to edit the board
 * @param props.isOwner - Whether current user is the board owner with full permissions
 * @param props.canShare - Whether current user has permission to share the board
 * @param props.userRole - The current user's role on this kanban board
 * @param props.isPipelineSurface - Whether the active surface is the PIPELINE surface (SE-5489); forks the center between the chip switcher and BoardTabs
 * @param props.onUpdateBoardName - Async callback fired when user saves a new board name
 * @param props.onBoardSelect - Callback fired when user selects a different board tab
 * @param props.onCreateBoard - Callback fired when user clicks create new board button
 * @param props.onMoreBoardOptions - Callback fired when user clicks board options menu
 * @param props.onDeleteBoard - Async callback fired when user confirms board deletion
 * @param props.onSharePress - Callback fired when user clicks share board button
 * @param props.viewMode - Which renderer the board is currently showing (SE-5491)
 * @param props.onViewModeChange - Callback fired with the newly selected renderer (SE-5491)
 */
const TopPanelContainer = (props: TopPanelContainerProps) => {
  /*
   * SE-6554: the `listKanbanTags` query that used to live here is gone with the
   * drawer's TAGS section. It existed only to build that section's chips, and
   * ran on every board render to populate a filter that could never bite —
   * `useSidebarState` strips tags before filtering, because the unplaced pool it
   * scopes holds players who carry no card tags. See the follow-ups ticket if
   * tag filtering is wanted for real.
   */
  const colors = useKanbanColors();

  // Use focused hooks for each responsibility
  const boardNameEditing = useBoardNameEditing({
    boardName: props.boardName,
    onUpdateBoardName: props.onUpdateBoardName,
  });

  const boardManagement = useBoardManagement({
    boardName: props.boardName,
    boards: props.boards,
    onUpdateBoardName: props.onUpdateBoardName,
    onBoardSelect: props.onBoardSelect,
    onCreateBoard: props.onCreateBoard,
    onMoreBoardOptions: props.onMoreBoardOptions,
    onDeleteBoard: props.onDeleteBoard,
    onStartEdit: boardNameEditing.handleStartEdit,
    onSaveEdit: boardNameEditing.handleSaveEdit,
    onCancelEdit: boardNameEditing.handleCancelEdit,
    onEditChange: boardNameEditing.handleEditChange,
    isEditing: boardNameEditing.isEditing,
    editValue: boardNameEditing.editValue,
    pipelineName: boardNameEditing.pipelineName,
  });

  const tagManagement = useTagManagement({ boardId: props.boardId });

  // The toolbar's Filters button belongs to the active view's Watchlist-parity
  // drawer, owned by MainContainer — SE-7144 for Table, SE-7405 for Kanban.
  // Both drawers are mounted unconditionally there, so every board has one
  // behind this button. Kept as typed Record lookups rather than nested
  // ternaries per CodeRabbit (PR #6846) and this repo's lookup-table guideline.
  const filterMode = resolveFilterMode(props.viewMode);
  const activeFilterCountByMode: Record<FilterMode, number> = {
    table: props.tableActiveFilterCount ?? 0,
    kanban: props.kanbanActiveFilterCount ?? 0,
  };
  const onFilterPressByMode: Record<FilterMode, (() => void) | undefined> = {
    table: props.onTableFiltersPress,
    kanban: props.onKanbanFiltersPress,
  };

  // SE-6488: the `+ Add player` primary CTA expands the SE-5490 add-players
  // rail, which is the board's existing "add a player" surface. Routed through
  // the reactive var rather than a prop-drilled callback so the header does not
  // have to reach across MainView into the rail.
  const addPlayersPanel = useAddPlayersPanelState();
  const handleAddPlayer = useCallback(
    () => addPlayersPanel.requestExpand(props.boardId),
    [addPlayersPanel, props.boardId]
  );

  const categoryLabel = useMemo(
    () => resolvePipelineCategoryLabel(props.pipelineTemplate),
    [props.pipelineTemplate]
  );

  // SE-6488: the toolbar row's dropdown state + row models. Lives in a hook
  // rather than a nested toolbar component so the header adds no new
  // renderable surface (see the hook's module doc).
  const toolbar = useBoardToolbar({
    sort: props.boardSort ?? DEFAULT_BOARD_SORT,
    onSortChange: props.onBoardSortChange ?? noOpSort,
    onSortDirectionChange: props.onBoardSortDirectionChange,
    boardId: props.boardId,
    isRosterBoard: props.isRosterBoard,
    isOwner: props.isOwner,
    canEdit: props.canEdit,
    canShare: props.canShare,
    onSharePress: props.onSharePress,
    onManageTags: tagManagement.handleManageTags,
  });

  // SE-6863: the shared default card order rows join the same overflow menu,
  // on the pipeline surface only (the server refuses other board types).
  const cardOrderActions = useBoardCardOrderActions({
    boardId: props.boardId,
    isPipelineBoard: props.isPipelineSurface === true,
    // "none" is the server order, which is what a publish sends.
    isServerOrder: (props.boardSort ?? DEFAULT_BOARD_SORT).option === "none",
    isMenuOpen: toolbar.isOverflowOpen,
  });
  const overflowItems = useMemo(
    () => [...toolbar.overflowItems, ...cardOrderActions.items],
    [toolbar.overflowItems, cardOrderActions.items]
  );

  return (
    <>
      <TopPanelView
        pipelineName={boardNameEditing.pipelineName}
        isEditing={boardNameEditing.isEditing}
        editValue={boardNameEditing.editValue}
        boards={props.boards || []}
        hasUnreachableBoards={props.hasUnreachableBoards}
        isRosterBoard={props.isRosterBoard}
        isPipelineSurface={props.isPipelineSurface}
        boardId={props.boardId}
        categoryLabel={categoryLabel}
        playerCount={props.playerCount}
        colors={colors}
        onStartEdit={boardNameEditing.handleStartEdit}
        onSaveEdit={boardNameEditing.handleSaveEdit}
        onCancelEdit={boardNameEditing.handleCancelEdit}
        onEditChange={boardNameEditing.handleEditChange}
        onBoardSelect={boardManagement.handleBoardSelect}
        onCreateBoard={boardManagement.handleCreateBoard}
        onMoreBoardOptions={boardManagement.handleMoreBoardOptions}
        onRenameBoard={boardManagement.handleRenameBoard}
        onDeleteBoard={boardManagement.handleDeleteBoard}
        activeFilterCount={activeFilterCountByMode[filterMode]}
        isOwner={props.isOwner}
        userRole={props.userRole}
        onFilterPress={onFilterPressByMode[filterMode]}
        viewMode={props.viewMode}
        onViewModeChange={props.onViewModeChange}
        boardSearchQuery={props.boardSearchQuery}
        onBoardSearchChange={props.onBoardSearchChange}
        onAddPlayer={handleAddPlayer}
        sortLabel={toolbar.sortLabel}
        isSortActive={toolbar.isSortActive}
        sortDirection={toolbar.sortDirection}
        sortOptions={toolbar.sortOptions}
        isSortOpen={toolbar.isSortOpen}
        onSortToggle={toolbar.onSortToggle}
        onSortClose={toolbar.onSortClose}
        overflowItems={overflowItems}
        isOverflowOpen={toolbar.isOverflowOpen}
        onOverflowToggle={toolbar.onOverflowToggle}
        onOverflowClose={toolbar.onOverflowClose}
        canSeeActivity={toolbar.canSeeActivity}
      />

      {tagManagement.tagManagerComponent}
      {boardManagement.deleteDialogComponent}
    </>
  );
};

export default TopPanelContainer;
